/**
 * Analytics data layer for unit management dashboard
 * Calculates metrics, aggregates interest data, and processes time-series data
 */
import { supabase } from '@/integrations/supabase/client';
import { getAllUnitsForAdmin } from './units';
import type { PropertyUnit } from '@/lib/units';
import type { Json } from '@/integrations/supabase/types';

export interface UnitInterestRecord {
  id: string;
  unit_id: string;
  contact_email: string;
  contact_phone: string | null;
  view_count: number | null;
  click_count: number | null;
  engagement_score: number | null;
  form_submitted: boolean | null;
  meeting_scheduled: boolean | null;
  first_viewed_at: string;
  last_viewed_at: string;
  lead_source: Json | null;
  created_at: string;
  updated_at: string;
}

// Re-export PropertyUnit for components
export type { PropertyUnit } from '@/lib/units';

export interface InventoryMetrics {
  total: number;
  available: number;
  reserved: number;
  sold: number;
  availablePercent: number;
  reservedPercent: number;
  soldPercent: number;
}

export interface RevenueMetrics {
  totalPortfolioValue: number;
  soldValue: number;
  reservedValue: number;
  availableValue: number;
  averagePrice: number;
  averagePriceByCategory: {
    '1bed': number;
    '2bed': number;
    townhouse: number;
  };
  pricePerSqFt: number;
}

export interface CategoryDistribution {
  category: '1bed' | '2bed' | 'townhouse';
  total: number;
  available: number;
  reserved: number;
  sold: number;
  averagePrice: number;
}

export interface BlockDistribution {
  block: 'A' | 'B';
  total: number;
  available: number;
  reserved: number;
  sold: number;
}

export interface FloorDistribution {
  floor: 'GF' | 'second' | 'third' | 'fourth';
  total: number;
  available: number;
  reserved: number;
  sold: number;
}

export interface ViewTypeDistribution {
  viewType: 'lagoon' | 'canal';
  total: number;
  available: number;
  reserved: number;
  sold: number;
}

export interface UnitInterestMetrics {
  unitId: string;
  unitNumber: string;
  viewCount: number;
  clickCount: number;
  engagementScore: number;
  uniqueContacts: number;
  formSubmissions: number;
}

export interface ConversionMetrics {
  availableToReserved: number;
  reservedToSold: number;
  overallConversion: number;
  averageDaysInAvailable: number | null;
  averageDaysInReserved: number | null;
}

export interface AnalyticsData {
  inventory: InventoryMetrics;
  revenue: RevenueMetrics;
  categoryDistribution: CategoryDistribution[];
  blockDistribution: BlockDistribution[];
  floorDistribution: FloorDistribution[];
  viewTypeDistribution: ViewTypeDistribution[];
  unitInterest: UnitInterestMetrics[];
  conversion: ConversionMetrics;
  units: PropertyUnit[];
}

/**
 * Calculate inventory metrics from units
 */
export function calculateInventoryMetrics(units: PropertyUnit[]): InventoryMetrics {
  const total = units.length;
  const available = units.filter((u) => u.availability === 'available').length;
  const reserved = units.filter((u) => u.availability === 'reserved').length;
  const sold = units.filter((u) => u.availability === 'sold').length;

  return {
    total,
    available,
    reserved,
    sold,
    availablePercent: total > 0 ? (available / total) * 100 : 0,
    reservedPercent: total > 0 ? (reserved / total) * 100 : 0,
    soldPercent: total > 0 ? (sold / total) * 100 : 0,
  };
}

/**
 * Calculate revenue metrics from units
 */
export function calculateRevenueMetrics(units: PropertyUnit[]): RevenueMetrics {
  const unitsWithPrice = units.filter((u) => u.price && u.price > 0);
  const totalPortfolioValue = units.reduce((sum, u) => sum + (u.price || 0), 0);
  const soldValue = units
    .filter((u) => u.availability === 'sold')
    .reduce((sum, u) => sum + (u.price || 0), 0);
  const reservedValue = units
    .filter((u) => u.availability === 'reserved')
    .reduce((sum, u) => sum + (u.price || 0), 0);
  const availableValue = units
    .filter((u) => u.availability === 'available')
    .reduce((sum, u) => sum + (u.price || 0), 0);

  const averagePrice = unitsWithPrice.length > 0
    ? unitsWithPrice.reduce((sum, u) => sum + (u.price || 0), 0) / unitsWithPrice.length
    : 0;

  // Calculate average price by category
  const categoryPrices: Record<string, number[]> = {
    '1bed': [],
    '2bed': [],
    townhouse: [],
  };

  unitsWithPrice.forEach((u) => {
    if (u.category && categoryPrices[u.category]) {
      categoryPrices[u.category].push(u.price || 0);
    }
  });

  const averagePriceByCategory = {
    '1bed':
      categoryPrices['1bed'].length > 0
        ? categoryPrices['1bed'].reduce((a, b) => a + b, 0) / categoryPrices['1bed'].length
        : 0,
    '2bed':
      categoryPrices['2bed'].length > 0
        ? categoryPrices['2bed'].reduce((a, b) => a + b, 0) / categoryPrices['2bed'].length
        : 0,
    townhouse:
      categoryPrices.townhouse.length > 0
        ? categoryPrices.townhouse.reduce((a, b) => a + b, 0) / categoryPrices.townhouse.length
        : 0,
  };

  // Calculate price per square foot
  const unitsWithArea = units.filter((u) => u.area_total && u.area_total > 0 && u.price && u.price > 0);
  const pricePerSqFt =
    unitsWithArea.length > 0
      ? unitsWithArea.reduce((sum, u) => sum + (u.price || 0) / (u.area_total || 1), 0) /
        unitsWithArea.length
      : 0;

  return {
    totalPortfolioValue,
    soldValue,
    reservedValue,
    availableValue,
    averagePrice,
    averagePriceByCategory,
    pricePerSqFt,
  };
}

/**
 * Calculate category distribution
 */
export function calculateCategoryDistribution(units: PropertyUnit[]): CategoryDistribution[] {
  const categories: ('1bed' | '2bed' | 'townhouse')[] = ['1bed', '2bed', 'townhouse'];
  
  return categories.map((category) => {
    const categoryUnits = units.filter((u) => u.category === category);
    const unitsWithPrice = categoryUnits.filter((u) => u.price && u.price > 0);
    const averagePrice =
      unitsWithPrice.length > 0
        ? unitsWithPrice.reduce((sum, u) => sum + (u.price || 0), 0) / unitsWithPrice.length
        : 0;

    return {
      category,
      total: categoryUnits.length,
      available: categoryUnits.filter((u) => u.availability === 'available').length,
      reserved: categoryUnits.filter((u) => u.availability === 'reserved').length,
      sold: categoryUnits.filter((u) => u.availability === 'sold').length,
      averagePrice,
    };
  });
}

/**
 * Calculate block distribution
 */
export function calculateBlockDistribution(units: PropertyUnit[]): BlockDistribution[] {
  const blocks: ('A' | 'B')[] = ['A', 'B'];
  
  return blocks.map((block) => {
    const blockUnits = units.filter((u) => u.block === block);
    return {
      block,
      total: blockUnits.length,
      available: blockUnits.filter((u) => u.availability === 'available').length,
      reserved: blockUnits.filter((u) => u.availability === 'reserved').length,
      sold: blockUnits.filter((u) => u.availability === 'sold').length,
    };
  });
}

/**
 * Calculate floor distribution
 */
export function calculateFloorDistribution(units: PropertyUnit[]): FloorDistribution[] {
  const floors: ('GF' | 'second' | 'third' | 'fourth')[] = ['GF', 'second', 'third', 'fourth'];
  
  return floors.map((floor) => {
    const floorUnits = units.filter((u) => u.floor === floor);
    return {
      floor,
      total: floorUnits.length,
      available: floorUnits.filter((u) => u.availability === 'available').length,
      reserved: floorUnits.filter((u) => u.availability === 'reserved').length,
      sold: floorUnits.filter((u) => u.availability === 'sold').length,
    };
  });
}

/**
 * Calculate view type distribution
 */
export function calculateViewTypeDistribution(units: PropertyUnit[]): ViewTypeDistribution[] {
  const viewTypes: ('lagoon' | 'canal')[] = ['lagoon', 'canal'];
  
  return viewTypes.map((viewType) => {
    const viewUnits = units.filter((u) => u.view_type === viewType);
    return {
      viewType,
      total: viewUnits.length,
      available: viewUnits.filter((u) => u.availability === 'available').length,
      reserved: viewUnits.filter((u) => u.availability === 'reserved').length,
      sold: viewUnits.filter((u) => u.availability === 'sold').length,
    };
  });
}

/**
 * Fetch and aggregate unit interest tracking data
 */
export async function getUnitInterestMetrics(
  units: PropertyUnit[],
  startDate?: Date,
  endDate?: Date
): Promise<UnitInterestMetrics[]> {
  try {
    let query = supabase
      .from('unit_interest_tracking')
      .select('*');

    if (startDate) {
      query = query.gte('first_viewed_at', startDate.toISOString());
    }
    if (endDate) {
      query = query.lte('first_viewed_at', endDate.toISOString());
    }

    const { data: interestData, error } = await query;

    if (error) {
      console.error('Error fetching unit interest data:', error);
      return [];
    }

    // Aggregate by unit_id
    const interestMap = new Map<string, UnitInterestMetrics>();

    (interestData || []).forEach((record: UnitInterestRecord) => {
      const existing = interestMap.get(record.unit_id) || {
        unitId: record.unit_id,
        unitNumber: '',
        viewCount: 0,
        clickCount: 0,
        engagementScore: 0,
        uniqueContacts: 0,
        formSubmissions: 0,
      };

      existing.viewCount += record.view_count || 0;
      existing.clickCount += record.click_count || 0;
      existing.engagementScore = Math.max(existing.engagementScore, record.engagement_score || 0);
      existing.uniqueContacts += 1;
      if (record.form_submitted) {
        existing.formSubmissions += 1;
      }

      interestMap.set(record.unit_id, existing);
    });

    // Map unit IDs to unit numbers
    const unitMap = new Map(units.map((u) => [u.id, u.unit_number]));
    const metrics = Array.from(interestMap.values()).map((metric) => ({
      ...metric,
      unitNumber: unitMap.get(metric.unitId) || 'Unknown',
    }));

    // Sort by engagement score descending
    return metrics.sort((a, b) => b.engagementScore - a.engagementScore);
  } catch (error) {
    console.error('Error processing unit interest metrics:', error);
    return [];
  }
}

/**
 * Calculate conversion metrics
 */
export function calculateConversionMetrics(units: PropertyUnit[]): ConversionMetrics {
  const available = units.filter((u) => u.availability === 'available').length;
  const reserved = units.filter((u) => u.availability === 'reserved').length;
  const sold = units.filter((u) => u.availability === 'sold').length;

  const availableToReserved = available > 0 ? (reserved / (available + reserved + sold)) * 100 : 0;
  const reservedToSold = reserved > 0 ? (sold / (reserved + sold)) * 100 : 0;
  const overallConversion = units.length > 0 ? (sold / units.length) * 100 : 0;

  // Calculate average days in each stage (if timestamps are available)
  // This is a simplified calculation - in production, you'd track status change timestamps
  const averageDaysInAvailable = null; // Would need status change history
  const averageDaysInReserved = null; // Would need status change history

  return {
    availableToReserved,
    reservedToSold,
    overallConversion,
    averageDaysInAvailable,
    averageDaysInReserved,
  };
}

/**
 * Fetch all analytics data
 * Computes all metrics from units data
 */
export async function getAllAnalytics(
  startDate?: Date,
  endDate?: Date
): Promise<AnalyticsData | null> {
  try {
    // Fetch all units
    const { data: units, error: unitsError } = await getAllUnitsForAdmin();

    if (unitsError || !units || units.length === 0) {
      console.warn('No units found for analytics');
      return null;
    }

    // Calculate all metrics from units data
    const inventory = calculateInventoryMetrics(units);
    const revenue = calculateRevenueMetrics(units);
    const categoryDistribution = calculateCategoryDistribution(units);
    const blockDistribution = calculateBlockDistribution(units);
    const floorDistribution = calculateFloorDistribution(units);
    const viewTypeDistribution = calculateViewTypeDistribution(units);
    const unitInterest = await getUnitInterestMetrics(units, startDate, endDate);
    const conversion = calculateConversionMetrics(units);

    return {
      inventory,
      revenue,
      categoryDistribution,
      blockDistribution,
      floorDistribution,
      viewTypeDistribution,
      unitInterest,
      conversion,
      units,
    };
  } catch (error) {
    console.error('Error fetching analytics data:', error);
    return null;
  }
}

// Main API - returns data in expected format
export async function getAnalyticsData(
  startDate?: Date,
  endDate?: Date
): Promise<{ data: AnalyticsData | null; error: string | null }> {
  try {
    const data = await getAllAnalytics(startDate, endDate);
    return { data, error: null };
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return { data: null, error: errorMessage };
  }
}

